import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import ViewWrapper from 'views/ViewWrapper';
import { TextareaAutosize } from '@material-ui/core';
import { useState } from 'react';
import { Search as SearchIcon, GetApp as ExportIcon } from '@material-ui/icons';
import { Box } from '@material-ui/core';
import * as API from 'api';
import { useQuery } from 'react-query';
import { DateTime } from 'luxon';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import TableHead from '@material-ui/core/TableHead';
import Button from '@material-ui/core/Button';
const Users = API.Users();

const columnLinks = {
  stripe_customer_id: 'https://dashboard.stripe.com/customers/:id',
  stripe_subscription_id: 'https://dashboard.stripe.com/subscriptions/:id',
  stripe_account_id: 'https://dashboard.stripe.com/connect/accounts/:id',
  latest_invoice: 'https://dashboard.stripe.com/invoices/:id',
  plan_product: 'https://dashboard.stripe.com/products/:id',
  plan_id: 'https://dashboard.stripe.com/prices/:id',
  release_upc: '/manage-releases?upc=:id',
  email: '/groups?email=:id',
  artist_name: '/artists?search=:id',
  payee_name: '/payees?search=:id',
};

const styles = theme => ({
  paper: {
    overflow: 'hidden',
    padding: theme.spacing(2),
  },
  title: {
    paddingBottom: theme.spacing(2),
  },
});

const fixedCellStyle = {
  minWidth: '100px',
  maxWidth: '100px',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  textAlign: 'left',
};

const formatAmount = amountInCents => {
  if (typeof amountInCents !== 'number') return null;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amountInCents / 100);
};

const formatDate = unixSeconds => {
  if (!unixSeconds) return null;
  return DateTime.fromSeconds(unixSeconds).toFormat('MM/dd/yyyy, hh:mm a');
};

const mapStripeSubscriptionData = stripeData => {
  if (!stripeData || typeof stripeData !== 'object') {
    return {};
  }
  const plan = stripeData.items?.data?.[0]?.plan || {};

  let latestInvoice = stripeData.latest_invoice;
  if (typeof stripeData.latest_invoice === 'object') {
    latestInvoice = stripeData.latest_invoice.id;
  }

  let defaultPaymentMethod = stripeData.default_payment_method;
  if (typeof stripeData.default_payment_method === 'object') {
    defaultPaymentMethod = stripeData.default_payment_method.id;
  }

  return {
    stripe_subscription_id: stripeData.id,
    stripe_customer_id: stripeData.customer,
    default_payment_method: defaultPaymentMethod,
    latest_invoice: latestInvoice,
    start_date: formatDate(stripeData.start_date),
    status: stripeData.status,
    canceled_at: formatDate(stripeData.canceled_at),
    cancel_at_period_end: stripeData.cancel_at_period_end,
    cancellation_reason: stripeData.cancellation_details?.reason || null,
    plan_id: plan.id,
    plan_interval: plan.interval,
    plan_amount: formatAmount(plan.amount),
    // Concatenate all metadata into a string
    plan_metadata: Object.entries(plan.metadata || {})
      .map(([key, value]) => `${key}: ${value}`)
      .join(',  '),
    plan_nickname: plan.nickname,
    plan_product: plan.product,
  };
};

const mapAssetReleaseData = assetReleases => {
  return assetReleases.map(release => {
    return {
      release_id: release.asset_release_id,
      release_upc: release.asset_release_upc,
      artist_name: release.display_artist_name,
      release_date: release.release_date,
      title: release.title,
    };
  });
};

const mapServiceDealsData = serviceDeals => {
  return serviceDeals.map(deal => {
    return {
      name: deal.name,
      service_deal_type: deal.service_deal_type,
      is_active: deal.is_active,
      created_at: deal.created_at,
      expiration_date: deal.expiration_date,
      notes: deal.notes,
    };
  });
};

const mapPayeesData = payees => {
  return payees.map(payee => {
    return {
      payee_id: payee.user_group_payee_id,
      payee_name: payee.full_name,
      created_at: payee.created_at,
    };
  });
};

function UniversalLookupByEmail({ classes }) {
  const [emails, setEmails] = useState([]);
  const [textareaValue, setTextareaValue] = useState('');
  const [isExportingCsv, setIsExportingCsv] = useState(false);
  const isValidEmail = email => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleChange = e => {
    let value = e?.target?.value;
    setTextareaValue(value);
  };

  const handleSearch = () => {
    // the emails are separated by spaces, semicolons, or line breaks
    const emails = textareaValue.split(/[\n,; ]+/);
    const validEmails = emails.filter(email => isValidEmail(email)).map(email => email.toLowerCase());
    setEmails(validEmails);
  };

  const handleExportCsv = async () => {
    if (isExportingCsv) return;

    setIsExportingCsv(true);
    const emails = textareaValue.split(/[\n,; ]+/);

    const validEmails = emails.filter(email => isValidEmail(email)).map(email => email.toLowerCase());

    if (validEmails.length === 0) {
      setIsExportingCsv(false);
      alert('No valid emails found');
      return;
    }

    await Users.lookupByEmailsCsv(validEmails);
    setIsExportingCsv(false);
  };

  const { data, isLoading } = useQuery({
    queryKey: ['userInfo', emails],
    queryFn: () => Users.lookupByEmails(emails),
    enabled: emails?.length > 0,
  });

  const users = data?.users || [];

  const formatValue = (value, key) => {
    if (value === null || value === undefined || value === '') return '-';

    const date = new Date(value);
    if (!isNaN(date.getTime()) && typeof value === 'string' && value.includes('T')) {
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    }

    // If the value is an array, return a list of values
    if (Array.isArray(value)) {
      return value.map(v => formatValue(v, key)).join(', ');
    }

    // Object: recursively render key-value pairs
    if (typeof value === 'object') {
      return (
        <Box>
          <Table>
            <TableBody>
              <TableRow>
                {Object.entries(value).map(([k, v]) => (
                  <TableCell key={k} style={fixedCellStyle}>
                    {formatValue(v, k)}
                  </TableCell>
                ))}
              </TableRow>
            </TableBody>
          </Table>
        </Box>
      );
    }

    if (columnLinks[key]) {
      return (
        <a href={columnLinks[key].replace(':id', value)} target="_blank" rel="noopener noreferrer">
          {value}
        </a>
      );
    }

    return String(value);
  };

  const formatKey = key => {
    if (!isNaN(key)) return null; // Skip numeric keys
    return (
      key
        .replace(/_/g, ' ')
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ') + ':'
    );
  };

  const keysToIgnore = [
    'user_user_id',
    'userGroupMemberships',
    'stripe_subscription_record',
    'user_group_id',
    'accepted_terms_url',
    'accepted_distribution_terms_url',
    'accepted_opt_in_sync_terms_url',
    'kyc_data',
  ];

  const displayValues = object => {
    if (!object || Object.keys(object).length === 0) return <Box>No data</Box>;

    let list = Object.entries(object);

    let header = <></>;

    if (Array.isArray(object) && object.length > 0) {
      header = (
        <Table>
          <TableHead>
            <TableRow>
              {Object.keys(object[0]).map(key => (
                <TableCell key={key} style={fixedCellStyle}>
                  {formatKey(key)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
        </Table>
      );
    }

    list = list
      .filter(([key]) => !keysToIgnore.includes(key))
      .filter(([_key, value]) => value !== null && value !== undefined) // Ignore null and undefined values
      .map(([key, value]) => (
        <Box key={key} p={0.2}>
          <strong>{formatKey(key)}</strong> {formatValue(value, key)}
        </Box>
      ));

    return (
      <>
        {header}
        {list}
      </>
    );
  };

  return (
    <ViewWrapper>
      <Grid item xs={12}>
        <Paper className={classes.paper}>
          <Typography className={classes.title} variant="h5">
            Universal Lookup by Email
          </Typography>
          <Box display="flex" flexDirection="column" justifyContent="space-between">
            <TextareaAutosize
              variant="outlined"
              label="Search"
              placeholder="Enter user or group emails separated by spaces, semicolons, or line breaks"
              style={{ minWidth: '400px', width: '100%', padding: 10, border: '1px solid #ccc', borderRadius: 4 }}
              value={textareaValue}
              onChange={handleChange}
              minRows={3}
            />
            <Button variant="contained" style={{ marginTop: 5, width: 150 }} onClick={() => handleSearch()}>
              <SearchIcon /> Search
            </Button>
            <Button
              variant="contained"
              style={{ marginTop: 5, width: 150 }}
              disabled={isExportingCsv}
              onClick={() => handleExportCsv()}>
              <ExportIcon /> Export CSV
            </Button>
          </Box>
          {(isLoading || isExportingCsv) && <Box>Loading...</Box>}
          {users?.map(userData => {
            const user = userData?.user || {};
            const subscription = userData?.subscription || {};
            const group = userData?.group || {};
            const groupMemberships = userData?.groupMemberships || [];
            const stripeSubscriptionRecord = subscription?.stripe_subscription_record || {};
            const paymentAccount = userData?.paymentAccount || {};
            const payees = userData?.payees || [];
            const assetReleases = userData?.assetReleases || [];
            const serviceDeals = userData?.serviceDeals || [];

            return (
              <Box key={user.user_user_id} mt={1} style={{ border: '1px solid #ccc', padding: 10, borderRadius: 4 }}>
                <Box pt={1}>
                  <Typography variant="h6">User</Typography>
                </Box>
                <Box>{displayValues(user)} </Box>
                <Box pt={1}>
                  <Typography variant="h6">Group</Typography>
                </Box>
                <Box>
                  <Box>{displayValues(group)} </Box>
                </Box>
                <Box pt={1}>
                  <Typography variant="h6">Group Memberships</Typography>
                </Box>
                <Box>
                  <Box>{displayValues(groupMemberships)}</Box>
                </Box>
                <Box pt={1}>
                  <Typography variant="h6">Service Deals</Typography>
                </Box>
                <Box>
                  <Box>{displayValues(mapServiceDealsData(serviceDeals))} </Box>
                </Box>

                <Box pt={1}>
                  <Typography variant="h6">Payment Account</Typography>
                </Box>
                <Box>
                  <Box>{displayValues(paymentAccount)} </Box>
                </Box>
                <Box pt={1}>
                  <Typography variant="h6">Subscription</Typography>
                </Box>
                <Box>
                  <Box>{displayValues(subscription)}</Box>
                </Box>
                <Box pt={1}>
                  <Typography variant="h6">Payees</Typography>
                </Box>
                <Box>
                  <Box>{displayValues(mapPayeesData(payees))}</Box>
                </Box>
                <Box pt={1}>
                  <Typography variant="h6">Asset Releases</Typography>
                </Box>
                <Box>
                  <Box>{displayValues(mapAssetReleaseData(assetReleases))} </Box>
                </Box>
                <Box pt={1}>
                  <Typography variant="h6">Stripe Subscription</Typography>
                </Box>
                <Box>
                  <Box>{displayValues(mapStripeSubscriptionData(stripeSubscriptionRecord))}</Box>
                </Box>
              </Box>
            );
          })}
        </Paper>
      </Grid>
    </ViewWrapper>
  );
}

UniversalLookupByEmail.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(UniversalLookupByEmail);
